import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLock, faTimes, faGem, faChevronLeft, faArrowUp, faHourglassHalf, faEllipsisH, faCheck } from '@fortawesome/free-solid-svg-icons'
import { usePrototype } from './PrototypeContext'
import * as data from '../../data/prototypeData'
import { DEFAULT_PROGRESSION as P, structureNames } from '../../rules/progression/config'
import { placeLot, removeLot, placeStructure, removeStructure, placedFootprints, footprintAt, warHive, hiveYield, hiveLevel, nextArena, toPlace, ROTATIONS } from '../../rules/progression/hive'
import { startUpgrade, finishNow, upgradeCost, upgradeBlocker, roundsRemaining, finishNowPrice } from '../../rules/progression/upgrades'
import { warReadiness } from '../../rules/progression/warSetup'
import { SUBPLOT_COUNT, SUB_PER_PLOT, footprintCenter } from '../../rules/subplotGrid'
import { subCenter, hexPoints, pixelToSub, columnSeamPoints, pointsAttr, HEX_COLS } from '../../utils/hexGeometry'
import { LAYOUT, COLOR } from '../../config/uiTokens'
import { R, H, BOARD_W, BOARD_H, LAND, HAIRLINE, ringPoints } from '../v2/hex/geometry'
import { resourceColor } from '../../config/resourceVisuals'
import InvCard, { LotArt, StructureArt, capitalize, initials, lotName } from './InvCard'
import '../LobbyScreen.css'
import '../v2/BattleFx.css'
import '../v2/BattleScreen.css'
import '../v2/HexBoard.css'
import './prototype.css'

// ─── The Hive editor ─────────────────────────────────────────────────────────
// The player's Hive between wars, on the war board's own lattice, where it sits
// in a war. Above it, the inventory: a shelf of cards for each tab. New (only
// while something waits to be placed, and opened first then) holds what's
// owned but not on the Hive: lots to lay and structures, which go down at
// once. Lots: pick a type, tap to lay one, tap a lot to lift it. Structures:
// pick one, Place it; tap a placed one to pick it. Shelves close with locked
// cards still to unlock. Upgrades start and finish from the picked
// structure's strip. Under the Hive, why it matters: Hive Level (which moves
// the player up arenas), the arena's structure slots and the yield per round.
//
// Every edit runs through the same rules the server applies (progression/),
// shows at once, and goes to the server in order; the server's document
// replaces the local copy when the queue drains, or on a refusal.

const FIELD_Y0 = BOARD_H / 2
const FIELD_H = BOARD_H - FIELD_Y0
// The Hive's top edge (its highest hex's flat top): the inventory reaches
// right down to it, its hint line sitting just above the Hive.
const HIVE_TOP = Math.min(...Array.from({ length: SUBPLOT_COUNT }, (_, s) => subCenter('self', s, R).y)) - H / 2
const INV_STYLE = { bottom: `${(1 - HIVE_TOP / BOARD_H) * 100}%` }
// 1,240 → 1.2k; 12,400 → 12k.
const short = (n) => {
    n = Math.floor(n ?? 0)
    if (n < 1000) return String(n)
    if (n < 10000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`
    if (n < 1e6) return `${Math.floor(n / 1000)}k`
    return `${(n / 1e6).toFixed(1).replace(/\.0$/, '')}M`
}
const fmt = (n) => Math.floor(n ?? 0).toLocaleString('en-US')
// An upgrade bar shows at least this much green, so one just started reads as
// started rather than empty.
const UPGRADE_TIP = 0.02
// At least this many locked "?" cards close each shelf: more to unlock.
const MIN_LOCKED = 10

export default function HiveEditor() {
    const { player, setPlayer, screen, goHome, resume, openWarMenu } = usePrototype()
    const [local, setLocal] = useState(null)        // the Hive with edits the server hasn't answered yet
    const localRef = useRef(null)
    const queue = useRef(Promise.resolve())
    const inFlight = useRef(0)
    const [chosenTab, setTool] = useState(null)     // 'new' | 'lots' | 'structures'; null: New while anything waits, else Lots
    const [picked, setPicked] = useState(null)      // structure name
    const [lotType, setLotType] = useState(null)
    const [rotation, setRotation] = useState(0)
    // A structure placement not yet confirmed: { name, cell, rotation, from },
    // `from` being where it stood ({ cell, rotation }) or null for a new one.
    // It shows on the Hive glowing; ✓ sends it, ✗ drops it.
    const [draft, setDraft] = useState(null)
    const dragRef = useRef(null)                    // { pointerId, dx, dy } while the handle is held
    const [dragging, setDragging] = useState(false)
    const [message, setMessage] = useState(null)
    const [busy, setBusy] = useState(false)
    const svgRef = useRef(null)
    const shown = local ?? player

    // The board's fit, as HexBoard does it in a war: the box's width, or less
    // when the screen is too short for the board's aspect.
    // (Measured once the Hive has loaded and the box is there.)
    const boxRef = useRef(null)
    const [fitWidth, setFitWidth] = useState(null)
    const loaded = !!shown
    useLayoutEffect(() => {
        const el = boxRef.current
        if (!el || typeof ResizeObserver === 'undefined') return undefined
        const measure = () => {
            const w = el.clientWidth, h = el.clientHeight
            if (w > 0 && h > 0) setFitWidth(Math.min(w, h * (BOARD_W / BOARD_H)))
        }
        measure()
        const ro = new ResizeObserver(measure)
        ro.observe(el)
        return () => ro.disconnect()
    }, [loaded])

    const geo = useMemo(() => {
        const cells = []
        for (let s = 0; s < SUBPLOT_COUNT; s++) {
            const { x, y } = subCenter('self', s, R)
            cells.push({ s, cx: x, cy: y, pts: hexPoints(x, y, R) })
        }
        const seams = []
        for (let sc = SUB_PER_PLOT - 1; sc < HEX_COLS - 1; sc += SUB_PER_PLOT) seams.push(pointsAttr(columnSeamPoints(sc, R)))
        return { cells, seams }
    }, [])

    const footprints = useMemo(() => (shown ? placedFootprints(shown) : {}), [shown])
    const owner = useMemo(() => {
        const m = new Map()
        for (const [name, fp] of Object.entries(footprints)) for (const c of fp ?? []) m.set(c, name)
        return m
    }, [footprints])

    if (!shown) return <div className="pt-screen"><p className="pt-dim">Loading your Hive…</p></div>

    // The tab (New gone once it's empty), and what a tap on the Hive does: in
    // New, the picked card decides (a structure, or a lot type).
    const fresh = toPlace(shown)
    const hasNew = fresh.count > 0
    const tool = chosenTab === 'new' ? (hasNew ? 'new' : 'structures') : chosenTab ?? (hasNew ? 'new' : 'lots')
    const activeLot = tool === 'new' ? (fresh.lots[lotType] ? lotType : null) : lotType
    const mode = tool === 'new' ? (picked ? 'structures' : 'lots') : tool

    // Applies `rule` here at once and sends `remote` after any edits before it.
    function edit(rule, remote) {
        let next
        try { next = rule(localRef.current ?? player) } catch (e) { setMessage(e.message); return }
        localRef.current = next
        setLocal(next)
        setMessage(null)
        inFlight.current++
        queue.current = queue.current.then(async () => {
            try {
                const doc = await remote()
                if (--inFlight.current === 0) { localRef.current = null; setLocal(null); setPlayer(doc) }
            } catch (e) {
                inFlight.current--
                setMessage(e.message)
                localRef.current = null
                setLocal(null)
                try { setPlayer(await data.loadPlayer()) } catch { /* the watcher catches up */ }
            }
        })
    }

    // The pointer in board units (the field's viewBox).
    function boardXY(e) {
        const rect = svgRef.current?.getBoundingClientRect()
        if (!rect?.width || !rect?.height) return null
        return { x: (e.clientX - rect.left) * (BOARD_W / rect.width), y: FIELD_Y0 + (e.clientY - rect.top) * (FIELD_H / rect.height) }
    }
    function hexAt(x, y) {
        const hit = pixelToSub(x, y, R)
        return hit && hit.side === 'self' ? hit.s : null
    }
    function hexFromEvent(e) {
        const pt = boardXY(e)
        return pt ? hexAt(pt.x, pt.y) : null
    }

    // Picking the picked structure again (its card, or its hexes) lets it go.
    function pick(name) {
        if (draft) return setMessage(`Place the ${draft.name} (✓) or cancel it (✗) first.`)
        if (name === picked) return deselect()
        setPicked(name)
        setRotation(shown.hive.structures[name]?.rotation ?? 0)
        setMessage(null)
    }
    function deselect() {
        setPicked(null)
        setMessage(null)
    }
    // A structure not on the Hive goes down glowing where it fits nearest the
    // middle, waiting for ✓.
    function startDraft(name, rot) {
        const cell = centerFit(shown, name, rot)
        if (cell == null) {
            const lot = P.structures[name]?.extractorFor
            return setMessage(lot
                ? `The ${name} sits on ${lotName(lot)} lots. Lay some on your Hive first.`
                : `No room on the Hive for the ${name}.`)
        }
        setDraft({ name, cell, rotation: rot, from: null })
        return undefined
    }
    // New's cards: a lot type to lay, or a structure, which goes down at once.
    function pickNewLot(t) {
        if (draft) return setMessage(`Place the ${draft.name} (✓) or cancel it (✗) first.`)
        setPicked(null)
        setLotType(t)
        setMessage(null)
        return undefined
    }
    function pickNew(name) {
        if (draft || name === picked) return pick(name)
        pick(name)
        return startDraft(name, 0)
    }

    function tap(s) {
        if (draft) return undefined
        if (s == null) return mode === 'structures' ? deselect() : undefined
        if (mode === 'lots') {
            if (shown.hive.lots[s] != null) return edit(p => removeLot(p, s), () => data.removeLot(s))
            if (!activeLot) return setMessage('Pick a lot type below first.')
            return edit(p => placeLot(p, s, activeLot), () => data.placeLot(s, activeLot))
        }
        const there = owner.get(s)
        if (there) return pick(there)
        return deselect()
    }

    // ── The draft: a placement waiting for ✓ ──
    const draftOk = draft ? fits(shown, draft.name, draft.cell, draft.rotation) : null
    const shownFootprints = draft
        ? { ...footprints, [draft.name]: footprintAt(draft.name, draft.cell, draft.rotation) ?? [draft.cell] }
        : footprints
    // The structure with the move handle: the draft, or a picked one on the Hive.
    const handleFor = draft ? draft.name
        : mode === 'structures' && picked && shown.hive.structures[picked] ? picked : null

    // The handle pressed: the structure lifts (a draft from where it stood)
    // and follows the pointer, keeping the grip's offset from its anchor hex.
    function grabHandle(e) {
        e.preventDefault()
        e.stopPropagation()
        const pt = boardXY(e)
        if (!pt || !handleFor) return
        const now = draft ?? { name: handleFor, ...shown.hive.structures[handleFor], from: { ...shown.hive.structures[handleFor] } }
        const anchor = geo.cells[now.cell]
        dragRef.current = { pointerId: e.pointerId, dx: anchor.cx - pt.x, dy: anchor.cy - pt.y }
        try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* not capturable */ }
        setDraft({ name: now.name, cell: now.cell, rotation: now.rotation, from: now.from })
        setDragging(true)
        setMessage(null)
    }
    function moveHandle(e) {
        const g = dragRef.current
        if (!g || g.pointerId !== e.pointerId) return
        const pt = boardXY(e)
        const s = pt ? hexAt(pt.x + g.dx, pt.y + g.dy) : null
        if (s != null) setDraft(d => (d && d.cell !== s ? { ...d, cell: s } : d))
    }
    function dropHandle(e) {
        if (dragRef.current?.pointerId !== e.pointerId) return
        dragRef.current = null
        setDragging(false)
    }
    // ✓: the draft goes on the Hive (unless it ended where it started).
    function confirmDraft() {
        const d = draft
        if (!d || !draftOk) return
        setDraft(null)
        setPicked(null)
        if (d.from && d.from.cell === d.cell && d.from.rotation === d.rotation) return
        edit(p => placeStructure(p, d.name, d.cell, d.rotation), () => data.placeStructure(d.name, d.cell, d.rotation))
    }
    // ✗: a new structure leaves the Hive; a moved one stays where it was.
    function cancelDraft() {
        if (draft?.from) setRotation(draft.from.rotation)
        setDraft(null)
        setMessage(null)
    }

    const cap = Math.min(P.arenas[shown.arena].maxStructures, P.inputs.maxStructuresHardCap)
    const placedCount = Object.keys(shown.hive.structures).length
    const perRound = hiveYield(warHive(shown)).total
    const readiness = screen.placement ? warReadiness(shown, screen.placement.needed) : null
    // On the shelves, what waits to be placed comes first (tagged NEW), then
    // the rest, each in the config's order.
    const waiting = (yes) => (yes ? 0 : 1)
    const isNew = (name) => fresh.structures.includes(name)
    const owned = structureNames().filter(n => shown.structures[n])
        .sort((a, b) => waiting(isNew(a)) - waiting(isNew(b)))
    // How close each structure is to its next level (its scarcest cost), and
    // whether it can start now; none while it upgrades or at the top.
    const progress = Object.fromEntries(owned.map(n => [n, upgradeProgress(shown, n)]))
    const readyCount = owned.filter(n => progress[n]?.ready).length
    const placedLots = {}
    for (const t of Object.values(shown.hive.lots)) placedLots[t] = (placedLots[t] ?? 0) + 1
    const lotTypes = Object.keys(P.lotTypes).filter(t => (shown.lots[t] ?? 0) > 0)
        .sort((a, b) => waiting(fresh.lots[a]) - waiting(fresh.lots[b]))
    const level = hiveLevel(shown)
    const next = nextArena(shown)

    const toWar = async () => {
        setBusy(true)
        try { await queue.current; await resume(screen.retry) } finally { setBusy(false) }
    }

    const lockedCount = (unlockable) => Math.max(MIN_LOCKED, unlockable)
    const pickedStructure = mode === 'structures' && picked && shown.structures[picked] ? picked : null
    const newHint = 'Structures raise your Hive Level to climb arenas. Lots make yield in war.'
    const hint = message ?? (screen.firstMill && !shown.hive.structures['Lumber Mill']
        ? 'Place your lots and structures on your Hive to earn and level up'
        : mode === 'lots'
            ? (activeLot ? `Tap a spot on Hive to place ${lotName(activeLot)} lot.` : tool === 'new' ? newHint : 'Pick a lot, then tap the Hive.')
            : draft
                ? (dragging ? '' : draftOk ? `Drag the handle to move the ${draft.name}. ✓ places it, ✗ cancels.` : `The ${draft.name} can’t go there. Drag it somewhere else, or ✗ to cancel.`)
                : picked
                    ? (shown.hive.structures[picked] ? `Drag the handle to move the ${picked}.` : `Tap Place to put the ${picked} on your Hive.`)
                    : tool === 'new' ? newHint
                        : `You have ${placedCount} structure${placedCount === 1 ? '' : 's'} on your Hive. Arena ${shown.arena} allows ${cap} maximum.`)
    // Sent here by a war the Hive wasn't ready for: what's missing, then that
    // war again. Otherwise the war menu.
    const action = readiness && !readiness.ready
        ? <span className="pt-hive__need">{readiness.short > 0 ? `Place ${readiness.short} more structure${readiness.short === 1 ? '' : 's'} for this war.` : readiness.problems[0]}</span>
        : <button className="lobby-btn-primary pt-hive__go" disabled={busy} onClick={readiness ? toWar : openWarMenu}>Go to war</button>

    // The war screen's frame (BattleScreen): the same header, the same board
    // box and fit, so the Hive sits exactly where your Hive does in a war. The
    // inventory takes the top half, where their Hive would be.
    return (
        <main>
            <div className="centered">
                {/* A press anywhere off the Hive, the cards and the picked
                    structure's strip lets the picked structure go. */}
                <div className="bs__screen pt-hive" onPointerDown={(e) => {
                    if (picked && !draft && !e.target.closest('[data-testid=hive-field], .pt-inv__card, .pt-inv__detail, .pt-tabs, .pt-hive__handle, .pt-hive__confirm')) deselect()
                }}>
                    <header className="bs__header pt-hive__head">
                        <button className="pt-back" onClick={goHome} aria-label="Back to home">
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <div className="pt-res" aria-label="Your resources">
                            <ResChip icon={<span className="pt-res__coin">$</span>} n={shown.bucks} label="Bucks" />
                            <ResChip icon={<FontAwesomeIcon icon={faGem} className="pt-res__gem" />} n={shown.gems} label="Gems" />
                        </div>
                    </header>

                    <div ref={boxRef} className="bg__container hexb" style={{ aspectRatio: `${BOARD_W} / ${BOARD_H}` }}>
                        <div className="pt-hive__board" style={{ aspectRatio: `${BOARD_W} / ${BOARD_H}`, ...(fitWidth != null ? { width: fitWidth } : null) }}>
                            <section className="pt-inv" style={INV_STYLE} aria-label="Inventory">
                                <div className="pt-tabs">
                                    {hasNew && (
                                        <button className={tool === 'new' ? 'pt-tab pt-tab--on' : 'pt-tab'} onClick={() => { setTool('new'); setMessage(null) }}>
                                            New<span className="pt-tab__new">{fresh.count}</span>
                                        </button>
                                    )}
                                    <button className={tool === 'lots' ? 'pt-tab pt-tab--on' : 'pt-tab'} onClick={() => { setTool('lots'); setMessage(null) }}>Lots</button>
                                    <button className={tool === 'structures' ? 'pt-tab pt-tab--on' : 'pt-tab'} onClick={() => { setTool('structures'); setMessage(null) }}>
                                        Structures{readyCount > 0 && <span className="pt-tab__ready">{readyCount} ready</span>}
                                    </button>
                                </div>
                                <div className="pt-inv__shelf" data-testid="inventory">
                                    {tool === 'new' ? (
                                        <>
                                            {Object.entries(fresh.lots).map(([t, n]) => (
                                                <InvCard key={`lot-${t}`} className={cardState(activeLot === t && !picked, false)} onClick={() => pickNewLot(t)}
                                                    art={<LotArt type={t} />} name={lotName(t)} sub={`${n} lot${n === 1 ? '' : 's'} to place`} />
                                            ))}
                                            {fresh.structures.map(name => (
                                                <InvCard key={name} className={cardState(picked === name, false)} onClick={() => pickNew(name)}
                                                    art={<StructureArt name={name} />} name={name} sub={`+${shown.structures[name].level} Hive Lv`} />
                                            ))}
                                        </>
                                    ) : tool === 'lots' ? (
                                        <>
                                            {lotTypes.map(t => (
                                                <InvCard key={t} className={cardState(lotType === t, false)} onClick={() => { setLotType(t); setMessage(null) }}
                                                    art={<LotArt type={t} />} name={lotName(t)} sub={`${placedLots[t] ?? 0}/${shown.lots[t]}`}
                                                    badge={fresh.lots[t] ? <NewBadge /> : null} />
                                            ))}
                                            <LockedCards n={lockedCount(Object.keys(P.lotTypes).length - lotTypes.length)} />
                                        </>
                                    ) : (
                                        <>
                                            {owned.map(name => (
                                                <InvCard key={name} className={cardState(picked === name, shown.hive.structures[name])} onClick={() => pick(name)}
                                                    art={<StructureArt name={name} />} name={name} sub={`Lv ${shown.structures[name].level}`}
                                                    badge={shown.structures[name].upgrade ? <UpgradingBadge /> : isNew(name) ? <NewBadge /> : progress[name]?.ready ? <ReadyBadge /> : null}
                                                    meter={progress[name]?.frac ?? null} />
                                            ))}
                                            <LockedCards n={lockedCount(structureNames().length - owned.length)} />
                                        </>
                                    )}
                                </div>
                                {pickedStructure && (
                                    <StructureActions name={pickedStructure} player={shown} pending={draft?.name === pickedStructure}
                                        onClose={() => { if (draft) cancelDraft(); deselect() }}
                                        onRotate={() => {
                                            // Turning it makes (or turns) a draft: ✓ to keep it.
                                            const r = (rotation + 1) % ROTATIONS
                                            setRotation(r)
                                            const at = shown.hive.structures[pickedStructure]
                                            if (draft) setDraft(d => ({ ...d, rotation: r }))
                                            else if (at) setDraft({ name: pickedStructure, cell: at.cell, rotation: r, from: { ...at } })
                                        }}
                                        onPlace={() => startDraft(pickedStructure, rotation)}
                                        onRemove={() => { setDraft(null); edit(p => removeStructure(p, pickedStructure), () => data.removeStructure(pickedStructure)) }}
                                        onUpgrade={() => edit(p => startUpgrade(p, pickedStructure), () => data.startUpgrade(pickedStructure))}
                                        onFinish={() => edit(p => finishNow(p, pickedStructure), () => data.finishUpgradeNow(pickedStructure))}
                                    />
                                )}
                                <p className={message ? 'pt-inv__hint pt-inv__hint--error' : 'pt-inv__hint'}>{hint}</p>
                            </section>

                            <svg
                                ref={svgRef}
                                className="pt-field"
                                viewBox={`0 ${FIELD_Y0} ${BOARD_W} ${FIELD_H}`}
                                preserveAspectRatio="xMidYMin meet"
                                data-testid="hive-field"
                                onPointerDown={(e) => tap(hexFromEvent(e))}
                            >
                                <defs>
                                    <filter id="pt-draft-glow" x="-50%" y="-50%" width="200%" height="200%">
                                        <feGaussianBlur stdDeviation="2.2" />
                                    </filter>
                                </defs>
                                <g>
                                    {geo.cells.map(({ s, pts }) => {
                                        const lot = shown.hive.lots[s]
                                        return <polygon key={s} points={pts} fill={lot ? resourceColor(lot) : LAND.self.fill} stroke={HAIRLINE} strokeWidth="0.5" />
                                    })}
                                </g>
                                <g fill="none" stroke={COLOR.BG} strokeWidth={LAYOUT.COL_GAP / 2} strokeLinejoin="round">
                                    {geo.seams.map((p, i) => <polyline key={i} points={p} />)}
                                </g>
                                {Object.entries(shownFootprints).map(([name, fp]) => {
                                    if (!fp) return null
                                    const sel = name === picked
                                    const pending = draft?.name === name
                                    const c = footprintCenter(fp)
                                    const { x, y } = subCenter('self', c, R)
                                    if (pending) {
                                        // Not on the Hive yet: glowing, yellow where it fits, red where it can't go.
                                        const col = draftOk ? '240, 224, 64' : '224, 80, 80'
                                        return (
                                            <g key={name} data-structure={name} data-pending="true" className="pt-field__draft">
                                                <g filter="url(#pt-draft-glow)">
                                                    {fp.map(s => <polygon key={s} points={geo.cells[s].pts} fill="none" stroke={`rgb(${col})`} strokeWidth="4" />)}
                                                </g>
                                                {fp.map(s => {
                                                    const { cx, cy, pts } = geo.cells[s]
                                                    return (
                                                        <g key={s}>
                                                            <polygon points={pts} fill={`rgba(${col}, 0.35)`} />
                                                            <polygon points={ringPoints(cx, cy, 1.5)} fill="none" stroke={`rgb(${col})`} strokeWidth="1.5" />
                                                        </g>
                                                    )
                                                })}
                                                <text x={x} y={y} className="pt-field__label" textAnchor="middle" dominantBaseline="central">{initials(name)}</text>
                                            </g>
                                        )
                                    }
                                    return (
                                        <g key={name} data-structure={name}>
                                            {fp.map(s => {
                                                const { cx, cy, pts } = geo.cells[s]
                                                return (
                                                    <g key={s}>
                                                        <polygon points={pts} fill={`rgba(${COLOR.STRUCT_SELF_RGB}, ${sel ? 0.4 : 0.22})`} />
                                                        <polygon points={ringPoints(cx, cy, 1.5)} fill="none" stroke="rgba(0, 0, 0, 0.5)" strokeWidth="3" />
                                                        <polygon points={ringPoints(cx, cy, 1.5)} fill="none" stroke={sel ? '#f0e040' : COLOR.STRUCT_SELF} strokeWidth="1.5" />
                                                    </g>
                                                )
                                            })}
                                            <text x={x} y={y} className="pt-field__label" textAnchor="middle" dominantBaseline="central">{initials(name)}</text>
                                        </g>
                                    )
                                })}
                            </svg>
                            {/* Upgrade bars on their own layer over the field: unlike the
                                field (whose seams run the board's full height), it isn't
                                clipped, so a bar can run past the Hive's edge. */}
                            <svg className="pt-field pt-field--over" viewBox={`0 ${FIELD_Y0} ${BOARD_W} ${FIELD_H}`} preserveAspectRatio="xMidYMin meet" aria-hidden="true">
                                {/* A structure being upgraded: a progress bar just under it,
                                    running past the Hive's edge when it's at one. */}
                                {Object.entries(shownFootprints).map(([name, fp]) => {
                                    const up = fp && shown.structures[name]?.upgrade
                                    if (!up) return null
                                    const xs = fp.map(s => geo.cells[s].cx), ys = fp.map(s => geo.cells[s].cy)
                                    const cx = (Math.min(...xs) + Math.max(...xs)) / 2
                                    const w = Math.max(R * 3, Math.max(...xs) - Math.min(...xs) + R * 1.8), h = R * 0.8
                                    const y = Math.max(...ys) + H / 2 + R * 0.15
                                    const x = cx - w / 2
                                    // One bar, filled by the rounds played, a divider at each
                                    // round's end (as in the structure panel's Upgrading box).
                                    const total = P.levels[up.toLevel].rounds
                                    const played = total - roundsRemaining(shown, name)
                                    const clip = `up-clip-${name.replace(/\W/g, '')}`
                                    return (
                                        <g key={`up-${name}`} className="pt-field__upgrade" style={{ pointerEvents: 'none' }} data-upgrading={name}>
                                            <clipPath id={clip}><rect x={x} y={y} width={w} height={h} rx={h / 2} /></clipPath>
                                            <g clipPath={`url(#${clip})`}>
                                                <rect x={x} y={y} width={w} height={h} fill="#3a3a3a" />
                                                <rect x={x} y={y} width={w * Math.max(UPGRADE_TIP, played / total)} height={h} fill="#5cc46c" />
                                                {Array.from({ length: total - 1 }, (_, k) => (
                                                    <rect key={k} x={x + (w * (k + 1)) / total - 0.35} y={y} width={0.7} height={h} fill="#111" />
                                                ))}
                                            </g>
                                            <rect x={x} y={y} width={w} height={h} rx={h / 2} fill="none" stroke="#f0e040" strokeWidth="0.9" />
                                        </g>
                                    )
                                })}
                            </svg>
                            {handleFor && shownFootprints[handleFor] && (
                                <HandleAndConfirm fp={shownFootprints[handleFor]} cells={geo.cells} name={handleFor}
                                    confirm={!!draft && !dragging} ok={!!draftOk}
                                    onGrab={grabHandle} onMove={moveHandle} onDrop={dropHandle}
                                    onConfirm={confirmDraft} onCancel={cancelDraft} />
                            )}
                        </div>
                    </div>

                    <div className="pt-hive__foot">
                        {/* Why the Hive matters: its level moves you up arenas, the
                            boxes say how many structures the arena lets it hold, and
                            its lots make yield every war round. */}
                        <div className="pt-hive__stats">
                            <div className="pt-score" title="Hive Level: the levels of the structures on your Hive added up. It moves you up arenas.">
                                <span>Hive Lv <strong>{level}</strong>{next && <>/{next.hiveLevel} → Arena {next.arena}</>}</span>
                                {next && <span className="pt-score__bar"><span style={{ width: `${Math.min(1, level / next.hiveLevel) * 100}%` }} /></span>}
                            </div>
                            <div className="pt-score">
                                <span className="pt-score__slots" aria-label={`${placedCount} of ${cap} structures on your Hive`}>
                                    Structures {Array.from({ length: cap }, (_, i) => <i key={i} className={i < placedCount ? 'pt-slot pt-slot--on' : 'pt-slot'} />)}
                                </span>
                                <span>Yield <strong>{perRound}</strong>/rd</span>
                            </div>
                        </div>
                        {action}
                    </div>
                </div>
            </div>
        </main>
    )
}

// The share of its next level's cost the player holds, by its scarcest part
// (every part must be full), and whether the upgrade can start now.
function upgradeProgress(player, name) {
    const st = player.structures[name]
    if (st.upgrade || !P.levels[st.level + 1]) return null
    const cost = upgradeCost(name, st.level + 1)
    const parts = [
        ...Object.entries(cost.raw ?? {}).map(([r, need]) => [player.raw[r] ?? 0, need]),
        ...(cost.bucks ? [[player.bucks, cost.bucks]] : []),
        ...Object.entries(cost.refined ?? {}).map(([r, need]) => [player.refined?.[r] ?? 0, need]),
    ].filter(([, need]) => need > 0)
    const frac = parts.length ? Math.min(...parts.map(([have, need]) => Math.min(1, have / need))) : 1
    return { frac, ready: !upgradeBlocker(player, name) }
}

// On a card for something not on the Hive yet that could go there.
const NewBadge = () => <span className="pt-inv__badge pt-inv__badge--new" title="Not on your Hive yet">NEW</span>

// On a structure's card when its upgrade can start: a green up arrow.
const ReadyBadge = () => (
    <span className="pt-inv__badge pt-inv__badge--ready" title="Ready to upgrade">
        <FontAwesomeIcon icon={faArrowUp} />
    </span>
)

// On a structure's card while it upgrades: an up arrow and an hourglass.
const UpgradingBadge = () => (
    <span className="pt-inv__badge" title="Upgrading">
        <FontAwesomeIcon icon={faArrowUp} />
        <FontAwesomeIcon icon={faHourglassHalf} />
    </span>
)

// One resource in the header: its mark and amount, its name under them.
function ResChip({ icon, n, label }) {
    return (
        <div className="pt-res__chip" title={`${fmt(n)} ${label.toLowerCase()}`}>
            <span className="pt-res__top">{icon}<strong>{short(n)}</strong></span>
            <span className="pt-res__label">{label}</span>
        </div>
    )
}

// A shelf card: on the Hive (the brown rim; structures only) and/or picked
// (a yellow ring, outside the brown one when both).
const cardState = (on, placed) => [placed && 'pt-inv__card--placed', on && 'pt-inv__card--on'].filter(Boolean).join(' ')

// The cards still to unlock: a "?" under a lock.
function LockedCards({ n }) {
    return Array.from({ length: n }, (_, i) => (
        <div key={`locked-${i}`} className="pt-inv__card pt-inv__card--locked" aria-label="Locked">
            <span className="pt-inv__art pt-inv__art--locked">?</span>
            <FontAwesomeIcon icon={faLock} className="pt-inv__lock" />
        </div>
    ))
}

// Whether `name` can stand at `cell` turned `rotation` (the rules say).
function fits(player, name, cell, rotation) {
    try { placeStructure(player, name, cell, rotation); return true } catch { return false }
}

// Where Place puts a structure: the free spot nearest the Hive's middle. The
// player drags it on from there.
const HIVE_MIDDLE = (() => {
    const pts = Array.from({ length: SUBPLOT_COUNT }, (_, s) => subCenter('self', s, R))
    return { x: pts.reduce((a, p) => a + p.x, 0) / pts.length, y: pts.reduce((a, p) => a + p.y, 0) / pts.length }
})()
const BY_MIDDLE = Array.from({ length: SUBPLOT_COUNT }, (_, s) => s)
    .map(s => { const { x, y } = subCenter('self', s, R); return { s, d: Math.hypot(x - HIVE_MIDDLE.x, y - HIVE_MIDDLE.y) } })
    .sort((a, b) => a.d - b.d).map(c => c.s)
function centerFit(player, name, rotation) {
    return BY_MIDDLE.find(s => fits(player, name, s, rotation)) ?? null
}

// Over the Hive, for the structure being placed or moved: the grip above it
// (three dots, drag to move) and, once it's set down, ✗ and ✓ under it.
// Positions are the board's, in percent of the board box.
function HandleAndConfirm({ fp, cells, name, confirm, ok, onGrab, onMove, onDrop, onConfirm, onCancel }) {
    const xs = fp.map(s => cells[s].cx), ys = fp.map(s => cells[s].cy)
    const cx = (Math.min(...xs) + Math.max(...xs)) / 2
    const pos = (x, y) => ({ left: `${(x / BOARD_W) * 100}%`, top: `${(y / BOARD_H) * 100}%` })
    return (
        <>
            <button className="pt-hive__handle" style={pos(cx, Math.min(...ys) - H / 2)} aria-label={`Move the ${name}`}
                onPointerDown={onGrab} onPointerMove={onMove} onPointerUp={onDrop} onPointerCancel={onDrop}>
                <FontAwesomeIcon icon={faEllipsisH} />
            </button>
            {confirm && (
                <div className="pt-hive__confirm" style={pos(cx, Math.max(...ys) + H / 2)}>
                    <button className="pt-hive__no" onClick={onCancel} aria-label="Cancel"><FontAwesomeIcon icon={faTimes} /></button>
                    <button className="pt-hive__yes" onClick={onConfirm} disabled={!ok} aria-label={`Place the ${name}`}><FontAwesomeIcon icon={faCheck} /></button>
                </div>
            )}
        </>
    )
}

// What a structure does, in a line.
function structureBlurb(def) {
    if (def.extractorFor) return `Multiplies ${def.extractorFor} yield from ${lotName(def.extractorFor)} lots (${P.inputs.extractorYieldMultiplier}x)`
    // No effect designed yet (structureEffects.js stubs it; the sheet's notes
    // are blank): say so rather than show nothing.
    return def.notes || `${def.family} structure. What it does hasn’t been designed yet.`
}

// The picked structure's panel: name and level, what it does, a meter per
// thing the next level costs (filled to what the player holds), Upgrade
// (live once every meter is full), and Place or Remove.
function StructureActions({ name, player, pending, onClose, onPlace, onRotate, onRemove, onUpgrade, onFinish }) {
    const def = P.structures[name]
    const st = player.structures[name]
    const placed = !!player.hive.structures[name]
    const blocker = upgradeBlocker(player, name)
    const nextLevel = P.levels[st.level + 1] ? st.level + 1 : null
    const cost = nextLevel && !st.upgrade ? upgradeCost(name, nextLevel) : null
    const meters = cost ? [
        ...Object.entries(cost.raw ?? {}).map(([r, need]) => ({ key: r, label: capitalize(r), have: player.raw[r] ?? 0, need, color: resourceColor(r) })),
        ...(cost.bucks ? [{ key: 'bucks', label: 'Bucks', have: player.bucks, need: cost.bucks, color: '#D4860A' }] : []),
        ...Object.entries(cost.refined ?? {}).map(([r, need]) => ({ key: r, label: capitalize(r), have: player.refined?.[r] ?? 0, need, color: '#8a83b8' })),
    ] : []
    const left = st.upgrade ? roundsRemaining(player, name) : 0
    const totalRounds = st.upgrade ? P.levels[st.upgrade.toLevel].rounds : 0
    // Full meters but still blocked (an arena's level cap): say why.
    const why = cost && blocker && blocker !== 'not enough resources' ? blocker : null
    return (
        <div className="pt-inv__detail">
            <button className="pt-inv__close" onClick={onClose} aria-label="Close">
                <FontAwesomeIcon icon={faTimes} />
            </button>
            <h3 className="pt-inv__detail-title">{name} <span>Lv {st.level}</span></h3>
            <p className="pt-inv__detail-blurb">{structureBlurb(def)}</p>
            {st.upgrade ? (
                // Upgrading: one yellow-ringed unit, the waiting bar and, as its
                // way out, Finish now.
                <div className="pt-upgrade-unit" aria-label={`Upgrading to Lv ${st.upgrade.toLevel}`}>
                    <UpgradingBadge />
                    <div className="pt-upgrading">
                        <span className="pt-upgrading__label">Upgrading • {left} rd{left === 1 ? '' : 's'}</span>
                        {/* One bar, filled by the rounds played, a divider at each
                            round's end. */}
                        <span className="pt-upgrading__track">
                            <span className="pt-upgrading__fill" style={{ width: `${Math.max(UPGRADE_TIP, (totalRounds - left) / totalRounds) * 100}%` }} />
                            {Array.from({ length: totalRounds - 1 }, (_, k) => (
                                <span key={k} className="pt-upgrading__tick" style={{ left: `${((k + 1) / totalRounds) * 100}%` }} />
                            ))}
                        </span>
                    </div>
                    <button className="pt-inv__big-btn pt-inv__big-btn--finish" onClick={onFinish} disabled={player.gems < finishNowPrice(player, name)}>
                        <span className="pt-finish__main">Finish now</span>
                        <span className="pt-finish__cost">{finishNowPrice(player, name)} gems</span>
                    </button>
                </div>
            ) : !nextLevel ? (
                <p className="pt-inv__sub">At the top level.</p>
            ) : (
                <div className="pt-meters">
                    {meters.map(m => (
                        <div key={m.key} className="pt-meter">
                            <span className="pt-meter__label">{m.label}</span>
                            <span className="pt-meter__track">
                                <span className="pt-meter__fill" style={{ width: `${Math.min(100, (m.have / m.need) * 100)}%`, background: m.color }} />
                            </span>
                            <span className="pt-meter__count">{fmt(Math.min(m.have, m.need))} / {fmt(m.need)}</span>
                        </div>
                    ))}
                    {why && <p className="pt-inv__sub">{capitalize(why)}.</p>}
                </div>
            )}
            <div className="pt-inv__detail-actions">
                {cost && <button className="pt-inv__big-btn pt-inv__big-btn--upgrade" onClick={onUpgrade} disabled={!!blocker}>Upgrade to Lv {nextLevel}</button>}
                {placed
                    ? <button className="pt-inv__big-btn pt-inv__big-btn--remove" onClick={onRemove}>Remove</button>
                    : !pending && <button className="pt-inv__big-btn" onClick={onPlace}>Place</button>}
                {placed && def.shape.length > 1 && <button className="pt-inv__big-btn" onClick={onRotate}>Rotate</button>}
            </div>
        </div>
    )
}
